import { useCallback, useEffect, useRef, useState } from "react";
import { fileUrl } from "@/lib/upload";

const MUTE_KEY = "kiosk_muted";
let ctx;
const ac = () => (ctx ||= new (window.AudioContext || window.webkitAudioContext)());

const beep = (freq, dur, type = "sine", when = 0, gain = 0.15) => {
  const c = ac();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(gain, c.currentTime + when);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + when + dur);
  o.connect(g).connect(c.destination);
  o.start(c.currentTime + when);
  o.stop(c.currentTime + when + dur);
};

const mk = (preset) => preset;
export const PRESETS = {
  classico: mk({
    label: "Classico", desc: "Beep arcade, trombe e campanelle da sala giochi.",
    spin: () => { for (let i = 0; i < 12; i++) beep(300 + i * 40, 0.08, "square", i * 0.12, 0.06); },
    reel_tick: () => { for (let i = 0; i < 18; i++) beep(1200 + (i % 3) * 150, 0.03, "square", i * 0.11, 0.035); },
    reel_stop: () => { beep(220, 0.12, "square", 0, 0.18); beep(440, 0.08, "triangle", 0.02, 0.12); },
    jackpot: () => { [523, 659, 784, 1047, 1319, 1568].forEach((f, i) => beep(f, 0.22, "square", i * 0.09, 0.14)); for (let i = 0; i < 10; i++) beep(1568 + (i % 2) * 262, 0.1, "triangle", 0.6 + i * 0.08, 0.1); },
    win: () => [523, 659, 784, 1047].forEach((f, i) => beep(f, 0.35, "triangle", i * 0.14, 0.2)),
    lose: () => [330, 262, 196].forEach((f, i) => beep(f, 0.4, "sawtooth", i * 0.25, 0.08)),
    printed: () => [880, 1320].forEach((f, i) => beep(f, 0.12, "triangle", i * 0.14, 0.18)),
  }),
  elegante: mk({
    label: "Elegante", desc: "Toni morbidi, carillon e accordi discreti.",
    spin: () => { for (let i = 0; i < 8; i++) beep(440 + i * 30, 0.2, "sine", i * 0.18, 0.05); },
    reel_tick: () => { for (let i = 0; i < 12; i++) beep(880, 0.04, "sine", i * 0.17, 0.025); },
    reel_stop: () => { beep(660, 0.25, "sine", 0, 0.1); beep(990, 0.3, "sine", 0.05, 0.06); },
    jackpot: () => [392, 494, 587, 784, 988, 1175].forEach((f, i) => beep(f, 0.6, "sine", i * 0.16, 0.1)),
    win: () => [392, 494, 587, 784].forEach((f, i) => beep(f, 0.7, "sine", i * 0.2, 0.12)),
    lose: () => [392, 349, 294].forEach((f, i) => beep(f, 0.6, "sine", i * 0.35, 0.06)),
    printed: () => [784, 1175].forEach((f, i) => beep(f, 0.3, "sine", i * 0.2, 0.1)),
  }),
  festa: mk({
    label: "Festa", desc: "Ritmo veloce, fanfare e coriandoli sonori.",
    spin: () => { for (let i = 0; i < 16; i++) beep(400 + (i % 4) * 120, 0.06, "sawtooth", i * 0.08, 0.05); },
    reel_tick: () => { for (let i = 0; i < 24; i++) beep(1000 + (i % 5) * 180, 0.03, "sawtooth", i * 0.08, 0.03); },
    reel_stop: () => { beep(180, 0.1, "sawtooth", 0, 0.2); beep(720, 0.1, "square", 0.03, 0.12); beep(1440, 0.08, "square", 0.08, 0.08); },
    jackpot: () => { [523, 659, 784, 1047, 784, 1047, 1319, 1568, 2093].forEach((f, i) => beep(f, 0.18, "sawtooth", i * 0.08, 0.13)); for (let i = 0; i < 16; i++) beep(1200 + Math.random() * 1200, 0.06, "triangle", 0.8 + i * 0.06, 0.08); },
    win: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => beep(f, 0.25, "square", i * 0.1, 0.18)); for (let i = 0; i < 8; i++) beep(1500 + (i % 3) * 300, 0.08, "triangle", 0.6 + i * 0.07, 0.1); },
    lose: () => [392, 330, 262, 196].forEach((f, i) => beep(f, 0.25, "square", i * 0.15, 0.08)),
    printed: () => [880, 1320, 1760].forEach((f, i) => beep(f, 0.1, "square", i * 0.1, 0.16)),
  }),
};
const DEFAULTS = PRESETS.classico;
const presetFx = (game) => PRESETS[game?.sound_preset] || DEFAULTS;

export const playDefault = (kind, preset) => { try { (PRESETS[preset] || DEFAULTS)[kind]?.(); } catch (e) { console.debug("Audio non disponibile", e); } };

export function useGameSounds(game) {
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const audioRef = useRef(null);
  useEffect(() => localStorage.setItem(MUTE_KEY, muted ? "1" : "0"), [muted]);
  const enabled = game?.sound_enabled !== false && !muted;

  const play = useCallback((kind) => {
    if (!enabled) return;
    const s = game?.sounds?.[kind];
    const src = fileUrl(s?.file_id) || s?.url;
    try {
      if (src) {
        audioRef.current?.pause();
        const a = new Audio(src);
        a.volume = 0.9;
        audioRef.current = a;
        a.play().catch(() => {});
      } else presetFx(game)[kind]?.();
    } catch (e) {
      console.debug("Audio non disponibile (autoplay bloccato)", e);
    }
  }, [enabled, game]);

  const stop = useCallback(() => { audioRef.current?.pause(); }, []);

  const ambientRef = useRef(null);
  const [ducked, setDucked] = useState(false);
  const ambSrc = fileUrl(game?.ambient?.file_id) || game?.ambient?.url || null;
  const ambVol = Math.min(1, Math.max(0, game?.ambient_volume ?? 0.35));
  useEffect(() => {
    if (!ambSrc || !enabled) { ambientRef.current?.pause(); ambientRef.current = null; return; }
    const a = new Audio(ambSrc);
    a.loop = true;
    a.volume = ambVol;
    ambientRef.current = a;
    const start = () => { a.play().catch(() => {}); };
    start();
    window.addEventListener("pointerdown", start, { once: true });
    return () => { a.pause(); window.removeEventListener("pointerdown", start); ambientRef.current = null; };
  }, [ambSrc, enabled, ambVol]);
  useEffect(() => {
    const a = ambientRef.current;
    if (!a) return;
    const target = ducked ? ambVol * 0.2 : ambVol;
    let raf;
    const step = () => { a.volume += (target - a.volume) * 0.15; if (Math.abs(a.volume - target) > 0.01) raf = requestAnimationFrame(step); else a.volume = target; };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [ducked, ambVol]);

  return { play, stop, muted, setMuted, setDucked, hasAmbient: !!ambSrc };
}
